import type { SxProps, Theme } from '@mui/material';

// Line height of the prompt text. Kept in one place because useTemplateRowVisibility derives its
// pixel thresholds from it (12 lines / 10 lines).
export const DASHBOARD_PROMPT_LINE_HEIGHT_PX = 26;

const PROMPT_CARD_PADDING_BLOCK_PX = 27;

// Flat bordered surface, deliberately not the `components/ui` Card primitive — that one bakes in a
// heavy drop shadow and spacing(4) padding. `alignItems: flex-start` is what keeps the AI icon and
// the "Press ↵" pill pinned to the first line as the textarea grows.
export const promptCardSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  backgroundColor: 'background.paper',
  border: theme => `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.l}px`,
  boxSizing: 'border-box',
  display: 'flex',
  gap: 2,
  padding: `${PROMPT_CARD_PADDING_BLOCK_PX}px 24px`,
  transition: theme => theme.transitions.create('border-color', { duration: 160 }),
  width: '100%',
  '&:focus-within': {
    borderColor: theme => theme.palette.brand[400],
  },
};

export const promptIconSx: SxProps<Theme> = {
  flexShrink: 0,
  height: 30,
  // Nudged so the ring optically centres on the first line rather than sitting above it.
  marginTop: '-2px',
  width: 30,
};

// Padding is zeroed on the inner textarea so its content-box height is exactly
// lines * DASHBOARD_PROMPT_LINE_HEIGHT_PX — the visibility hook measures that element directly.
export const promptInputSx: SxProps<Theme> = {
  color: 'text.primary',
  flex: 1,
  fontSize: 18,
  lineHeight: `${DASHBOARD_PROMPT_LINE_HEIGHT_PX}px`,
  minWidth: 0,
  padding: 0,
  '& .MuiInputBase-input': {
    padding: 0,
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme => theme.palette.neutral[400],
    opacity: 1,
  },
};

// The first-line action cluster: the Enter hint (or its busy replacement) beside the pointer submit
// button. Grouped so both stay pinned to the first line as the textarea grows, and so the button can
// never wrap under the hint on a narrow card. Carries the nudge that used to sit on the hint itself,
// which is what optically centres the row on the first line of prompt text.
export const promptActionsSx: SxProps<Theme> = {
  alignItems: 'center',
  alignSelf: 'flex-start',
  display: 'flex',
  flexShrink: 0,
  gap: 1,
  marginTop: '3px',
};

// Sized to the hint pill rather than to the Button preset's own 32px minimum, so the two read as one
// control cluster instead of a badge stuck beside a full-height button.
export const promptSubmitButtonSx: SxProps<Theme> = {
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  fontSize: 13,
  lineHeight: '18px',
  minHeight: 26,
  padding: '3px 12px',
};

// Replaces the submit hint while a dashboard is being generated. Same box metrics as the hint so
// the card does not reflow on the swap; only the palette changes to read as active rather than inert.
export const promptBusyHintSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: theme => theme.palette.brand[100],
  border: theme => `1px solid ${theme.palette.brand[200]}`,
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.brand[800],
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 13,
  gap: 0.75,
  lineHeight: '18px',
  padding: '3px 9px',
  whiteSpace: 'nowrap',
};

export const promptBusySpinnerSx: SxProps<Theme> = {
  color: theme => theme.palette.brand[600],
};

// A span, not a button: the hint is inert, and a focusable no-op would be a keyboard-nav trap. The
// pointer path is the sibling submit button, which is a real one.
export const promptSubmitHintSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: theme => theme.palette.neutral[100],
  border: theme => `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.neutral[500],
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 13,
  lineHeight: '18px',
  padding: '3px 9px',
  whiteSpace: 'nowrap',
};
